import { Portal, Stack } from '@mui/material';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';

import { Toast, type ToastMessage, type ToastProps } from './Toast.js';
import { ToastContext } from './ToastContext.js';
import { zIndex } from '../../theme/zIndex/zIndex.js';

type ToastProviderToast = { id: string; message: string | ToastMessage } & Partial<
  Omit<ToastProps, 'onClose' | 'message'>
>;

export const ToastProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastProviderToast[]>([]);

  const open = useCallback(
    (
      message: string | ToastMessage,
      options?: Partial<Omit<ToastProps, 'onClose' | 'message'>>
    ) => {
      setToasts((currentToasts) => [
        ...currentToasts,
        {
          // Note - random numbers don't guarantee uniqueness. This should be deterministic.
          id: Math.random().toString(),
          message,
          ...options,
        },
      ]);
    },
    []
  );

  const showSuccess = useCallback(
    (message: string | ToastMessage) => open(message, { variant: 'success' }),
    [open]
  );
  const showError = useCallback(
    (message: string | ToastMessage) => open(message, { variant: 'error' }),
    [open]
  );
  const showWarning = useCallback(
    (message: string | ToastMessage) => open(message, { variant: 'warning' }),
    [open]
  );
  const showInfo = useCallback(
    (message: string | ToastMessage) => open(message, { variant: 'info' }),
    [open]
  );

  const close = (id: string) =>
    setToasts((currentToasts) => currentToasts.filter((toast) => toast.id !== id));

  /**
   * Convert to `constate` and or remove this memo.
   */
  const contextValue = useMemo(
    () => ({
      open,
      showSuccess,
      showError,
      showWarning,
      showInfo,
    }),
    [open, showError, showInfo, showSuccess, showWarning]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      <Portal>
        <Stack
          gap={1}
          sx={{
            zIndex: zIndex.toast,
            position: 'fixed',
            alignItems: 'center',
            flexDirection: { md: 'column', xs: 'column-reverse' },
            top: { md: 'initial', xs: 48 },
            right: { md: 32, xs: 24 },
            left: { md: 'unset', xs: 24 },
            bottom: { md: 32, xs: 'unset' },
            width: { md: 'fit-content' },
          }}
        >
          {toasts.map(({ id, message, ...rest }) => (
            <Toast key={id} onClose={() => close(id)} message={message} {...rest} />
          ))}
        </Stack>
      </Portal>
    </ToastContext.Provider>
  );
};

ToastProvider.displayName = 'ToastProvider';
